<template>
  <PForm
    ref="pForm"
    :heading="`${formState.edit ? 'Edit' : 'Add'} File`"
    :primary-cta="submitBtn"
    :secondary-cta="
      label
        ? { label: label, onClick: label === 'Reset' ? resetForm : onClose }
        : {}
    "
    @close="onClose"
    @submit.prevent="onSubmit"
    @reset="resetForm"
  >
    <fieldset class="border-none q-ma-none q-pa-none">
      <legend
        v-if="initialState?.file_type"
        class="q-mb-sm text-neutral text-weight-medium"
      >
        Submit document for this
        {{ initialState?.file_type || "Entity" }}:
        {{ documentNumber || "-" }}
      </legend>
      <div class="row q-col-gutter-lg">
        <div class="col-6">
          <PSelectInput
            v-if="isDocumentTypeSelectable"
            v-model="formData.file_type"
            :options="documentTypeOptions"
            help="Select type of document"
            label="Type"
            placeholder="Purchase Order"
            required
            @update:model-value="initEntityFilter"
          />
        </div>
        <div class="col-6">
          <PSelectInput
            v-if="isDocumentSelectable"
            v-model="formData[FILE_TYPE_MAP[formData.file_type]?.key]"
            :emit-value="false"
            :map-options="false"
            :label="FILE_TYPE_MAP[formData.file_type]?.label"
            :options="unref(entityFilter.entities)"
            option-label="number"
            option-value="_id"
            help="Select document"
            :placeholder="FILE_TYPE_MAP[formData.file_type].label"
            required
            virtual-scroll-slice-size="25"
            virtual-scroll-item-size="53"
            :options-loading="unref(entityFilter.loading)"
            @virtual-scroll="entityFilter.onScroll"
            @filter="entityFilter.onFilter"
            @filter-abort="entityFilter.onFilterAbort"
            @update:model-value="onUpdateEntity"
          />
        </div>
        <div class="col-12">
          <PFile
            v-model="formData.file"
            :help="
              allowAddlMimeType
                ? 'Allowed file type are pdf, images, excel, csv'
                : 'Allowed file type are pdf, images'
            "
            label="File"
            :accept="
              allowAddlMimeType
                ? '.jpg, .pdf, image/*, .jpeg, .xlsx, .csv'
                : '.jpg, .pdf, image/*, .jpeg'
            "
            :validations="{
              key: 'name',
              required: true,
            }"
            @update:model-value="updateFile"
          />
          <PFile
            v-if="formData.file"
            v-model="formData.file"
            class="q-mt-xxs"
            variant="re-upload"
          />
        </div>
      </div>
    </fieldset>
  </PForm>
</template>

<script setup>
import { isEmpty } from "assets/scripts/utilities/helpers";
import { FILE_TYPE_MAP } from "assets/scripts/utilities/constants";
import fileFormModel from "src/modules/purchase-order/models/fileForm";

const props = defineProps({
  closable: {
    type: Boolean,
    default: false,
  },
  initialState: {
    type: Object,
    default: () => {
      return {};
    },
  },
  resettable: {
    type: Boolean,
    default: true,
  },
  toggle: {
    type: Object,
    default: () => {
      return {
        method: false,
        options: {},
      };
    },
  },
});

const pForm = ref(null);

const { formState, file } = storeToRefs(useFileStore());
const { postFile, putFile, toggleFileForm } = useFileStore();
const { rightDrawerComponent, rightDrawerComponentProps } =
  storeToRefs(useAppStore());

const { formData, isLoading, closeForm, resetForm, submitForm } = useForm({
  formModel: fileFormModel(props.initialState),
  entity: file,
  formRef: pForm,
  isForm: formState,
  post: postFile,
  put: putFile,
  toggleForm: toggleFileForm,
});

const destroyForm = () => {
  rightDrawerComponent.value = null;

  rightDrawerComponentProps.value = {};
};

const onClose = () => {
  closeForm();

  toggleMethod();
};

const onSubmit = async () => {
  if (unref(formData).file) {
    await submitForm();

    toggleMethod();
  }
};

const toggleMethod = () => {
  if (props.toggle.method) {
    props.toggle.method(props.toggle.options);
  } else {
    destroyForm();
  }
};

const documentTypeOptions = computed(() => {
  let options = [
    {
      label: "E-Invoice",
      value: "E-Invoice",
    },
  ];

  if (props.initialState?.po_id?._id) {
    options = [
      { label: "Purchase Order", value: "Purchase Order" },
      { label: "Purchase Bill", value: "Purchase Bill" },
      { label: "Proof of Delivery", value: "Proof of Delivery" },
      { label: "Credit Note - Only Vendor's", value: "Debit Note" },
      {
        label: "Debit Note - Only Customer's",
        value: "Credit Note",
      },
      { label: "Vendor’s E-way Bill", value: "E-Way Bill" },
    ];
  }

  return options;
});

const { updateFile } = useFile(formData);

let entityFilter = {
  entities: [],
  onFilter: () => {},
  onFilterAbort: () => {},
};

const initEntityFilter = () => {
  const { entity } = unref(FILE_TYPE_MAP)[unref(formData).file_type];

  const keys = [
    "invc_id",
    "ewaybill_id",
    "po_id",
    "bill_id",
    "pod_id",
    "cn_id",
    "dn_id",
  ];

  for (const key of keys) {
    if (!props.initialState?.[key]) {
      delete unref(formData)[key];
    }
  }

  const status_cd = ["active"];

  if (entity === "pod") {
    status_cd.push(...["received", "returns", "rejected"]);
  }

  entityFilter = useFilter({
    entity,
    options: {
      params: {
        po_id:
          entity !== "purchaseOrder"
            ? props.initialState?.po_id?._id || null
            : null,
        number: 1,
        status_cd: status_cd.join(","),
        requested_against: "purchaseBill",
      },
    },
  });
};
const isDocumentTypeSelectable = computed(() => {
  return !props.initialState.file_type;
});

const isDocumentSelectable = computed(() => {
  return (
    unref(formData)?.file_type &&
    !props.initialState?.[unref(FILE_TYPE_MAP)[unref(formData)?.file_type]?.key]
      ?._id
  );
});

const allowAddlMimeType = computed(() => {
  return ["Purchase Order", "Purchase Bill"].includes(
    unref(formData).file_type,
  );
});

const documentNumber = computed(() => {
  let documentNumber = "";

  documentNumber =
    props.initialState[unref(FILE_TYPE_MAP)[props.initialState?.file_type]?.key]
      ?.number ||
    props.initialState[unref(FILE_TYPE_MAP)[props.initialState?.file_type]?.key]
      ?.internal_number ||
    props.initialState[unref(FILE_TYPE_MAP)[props.initialState?.file_type]?.key]
      ?.po_info?.internal_number;

  return documentNumber;
});

const onUpdateEntity = (entity) => {
  if (!props.initialState?.po_id?._id) {
    unref(formData).po_id = entity?.po_id;
  }

  if (unref(formData).file_type === "Proof of Delivery") {
    unref(formData).invc_id = entity?.invc_id?._id || entity?.invc_id;
  }
};

onBeforeMount(() => {
  if (props.initialState?.file_type) {
    initEntityFilter();
  }
});

const submitBtn = computed(() => {
  return {
    loading: unref(isLoading),
  };
});

let label = ref("");

watch(
  () => props,
  () => {
    if (isEmpty(props.initialState)) {
      label.value = "Reset";
    } else if (props.closable) {
      label.value = "I don't have";
    }
  },
  { deep: true, immediate: true },
);
</script>
